import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../../redux/store";
import PermissionGate from "../../../../core/layouts/permission-gate";
import ToastComponent from "../../../../components/toast";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../../redux/features/common-slice";
import { UserRole } from "../../../../redux/features/user-slice";
import Table from "../../../../components/table";
import TableBodyCell from "../../../../components/table-body-cell";
import EmptyDataOverlay from "../../../../components/empty-data-overlay";
import Loading from "../../../../components/loading";
import Chip from "../../../../components/chip";
import {
  CommonStatus,
  teamStateAction,
} from "../../../../redux/features/other-slice";
import paths from "../../../../navigation/paths";
import { adminCat } from "../../../../core/utils/constants";
import {
  fetchAllScheduleReportsAction,
  getAllScheduleReportsState,
  ScheduleExportResponse,
} from "../../../../redux/features/schedule-export.slice";

export default function ScheduleReportsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allScheduleReportsState = useSelector(getAllScheduleReportsState);
  const errorState = useSelector(getErrorState);

  const fetchAllSchedules = useCallback(async () => {
    await dispatch(fetchAllScheduleReportsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchAllSchedules();
  }, [fetchAllSchedules]);

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: "data", path: null, title: "Data" },
        {
          id: "scheduleReports",
          path: null,
          title: "Schedule Reports",
        },
      ])
    );
    const fetchInitialData = async () => {
      await dispatch(
        teamStateAction({
          roles: [0, 1],
          status: [CommonStatus.Active, CommonStatus.Inactive],
        })
      );
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      <Helmet title={"All Schedules"} />
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          {
            id: "reportName",
            label: "Report Name",
            align: "text-left",
            width: "w-3/12",
          },
          {
            id: "frequency",
            label: "Frequency",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "period",
            label: "Period",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "owner",
            label: "Owner",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "reportType",
            label: "Type",
            align: "text-left",
            width: "w-1/12",
          },
        ]}
        body={
          allScheduleReportsState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : allScheduleReportsState.data.length > 0
            ? allScheduleReportsState.data.map(
                (schedule: ScheduleExportResponse, index: number) => {
                  return (
                    <tr
                      key={schedule.id}
                      className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                      onClick={() =>
                        navigate(`${paths.SCHEDULE_REPORTS}/${schedule.id}`)
                      }
                    >
                      <TableBodyCell exClassName="font-medium">
                        <div className="flex items-center space-x-2">
                          <label className="whitespace-normal break-words hover:underline cursor-pointer">
                            {schedule.reportName}
                          </label>
                          {schedule.isActive === false && (
                            <Chip
                              label="Inactive"
                              type="error"
                              exClassName="w-fit"
                            />
                          )}
                        </div>
                      </TableBodyCell>
                      <TableBodyCell>
                        <label className="whitespace-normal break-words">
                          {schedule.frequency.capitalizeWords()}
                        </label>
                      </TableBodyCell>
                      <TableBodyCell exClassName="">
                        <label className="whitespace-normal break-words hover:underline cursor-pointer">
                          {schedule.period.capitalizeWords()}
                        </label>
                        <br />
                      </TableBodyCell>
                      <TableBodyCell>
                        <label className="whitespace-normal break-words">
                          {schedule.owner
                            ? schedule.owner.name
                            : "Unknown Owner"}
                        </label>
                      </TableBodyCell>
                      <TableBodyCell>
                        <label className="whitespace-normal break-words">
                          {schedule.reportType}
                        </label>
                      </TableBodyCell>
                    </tr>
                  );
                }
              )
            : [
                <tr key="empty-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No schedules found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allSchedules" />
    </PermissionGate>
  );
}
